import axios from 'axios';
import { authService } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

function authHeaders() {
  const token = authService.getToken();
  return token
    ? { 'x-access-token': token, 'x-pp-client-id': 'privacypal-user-portal' }
    : {};
}

/**
 * Memory v2 vocabulary (docs/dev-tasks/EPIC-04-memory-v2-contract.md section 0).
 * The legacy category names are still accepted on read so facts written before
 * the overhaul keep rendering; `normalizeCategory` maps them.
 */
export type MemoryCategoryV2 = 'fact' | 'preference' | 'instruction' | 'event' | 'opinion';
export type MemoryCategoryLegacy = 'semantic' | 'procedural' | 'episodic-summary';
export type MemoryCategory = MemoryCategoryV2 | MemoryCategoryLegacy;
export type MemoryStatus = 'active' | 'quarantined' | 'invalidated' | 'sensitive' | 'archived';
export type MemorySensitivity = 'entity' | 'quasi' | 'special' | 'preference' | 'work' | 'procedural' | 'third_party';
export type MemoryDomain =
  | 'work' | 'coding' | 'health' | 'legal' | 'finance' | 'family' | 'education' | 'travel' | 'creative' | 'general';
export type MemorySource =
  | 'consolidation-v1' | 'consolidation-v2' | 'reflection' | 'user'
  | 'import:chatgpt' | 'import:claude' | 'import:gemini' | 'mcp';
export type MemoryImportPlatform = 'chatgpt' | 'claude' | 'gemini';
export type MemoryProfileLabel = 'working_context' | 'response_preferences' | 'standing_requests' | 'current_focus';

export const MEMORY_DOMAINS: readonly MemoryDomain[] = [
  'work', 'coding', 'health', 'legal', 'finance', 'family', 'education', 'travel', 'creative', 'general',
];
export const MEMORY_CATEGORIES: readonly MemoryCategoryV2[] = ['fact', 'preference', 'instruction', 'event', 'opinion'];
export const MEMORY_PROFILE_LABELS: readonly MemoryProfileLabel[] = [
  'working_context', 'response_preferences', 'standing_requests', 'current_focus',
];
/** Character limits per profile block (contract section 0, block `label`). */
export const MEMORY_PROFILE_CHAR_LIMITS: Record<MemoryProfileLabel, number> = {
  working_context: 320,
  response_preferences: 280,
  standing_requests: 320,
  current_focus: 280,
};

const LEGACY_CATEGORY: Record<string, MemoryCategoryV2> = {
  semantic: 'fact',
  procedural: 'instruction',
  'episodic-summary': 'event',
};

export function normalizeCategory(category: string | null | undefined): MemoryCategoryV2 {
  if (!category) return 'fact';
  if (LEGACY_CATEGORY[category]) return LEGACY_CATEGORY[category];
  return (MEMORY_CATEGORIES as readonly string[]).includes(category) ? (category as MemoryCategoryV2) : 'fact';
}

export type MemoryConfidence = 'high' | 'medium' | 'low';
/** Confidence bands are the contract's: high >= 0.8, medium >= 0.6, else low. */
export function confidenceBand(confidence: number | null | undefined): MemoryConfidence {
  const c = typeof confidence === 'number' ? confidence : 0;
  if (c >= 0.8) return 'high';
  if (c >= 0.6) return 'medium';
  return 'low';
}

export interface MemoryFact {
  id: string;
  factText: string;
  displayText: string;
  category: MemoryCategory;
  entityTwins: string[];
  entityValues: string[];
  importance: number;
  confidence: number;
  status: MemoryStatus;
  platform: string | null;
  sourceEpisodeId: string | null;
  validAt: number | null;
  invalidAt: number | null;
  observedAt: number | null;
  ingestedAt: number | null;
  lastRecalledAt: number | null;
  recallCount: number;
  /** Team (domain-scope) facts carry the promoting user's id. */
  ownerUserId?: string | null;
  /** 'user' for personal facts, 'domain:{companyId}' for team facts. */
  scope?: string | null;
  // ---- v2 props (contract section 4); every one optional so pre-v2 rows still type-check.
  sensitivity?: MemorySensitivity | null;
  domain?: MemoryDomain | null;
  subject?: 'self' | 'third_party' | null;
  sourceRole?: 'user' | 'user-corrected' | 'user-manual' | 'import' | null;
  sourceSurface?: string | null;
  sourceSurfaceClass?: string | null;
  audience?: string[] | null;
  trust?: 'captured' | 'corrected' | 'promoted' | 'manual' | 'imported' | null;
  injectable?: boolean | null;
  generalizedText?: string | null;
  isUserAuthored?: boolean | null;
  eventAt?: number | null;
  evidenceEpisodeIds?: string[] | null;
  evidenceQuote?: string | null;
  source?: MemorySource | string | null;
  schemaVersion?: number | null;
  lastConfirmedAt?: number | null;
  pinned?: boolean | null;
  guardDecision?: 'allow' | 'redact' | 'quarantine' | 'block' | null;
  guardReason?: string | null;
  supersededBy?: string | null;
}

export interface MemorySettings {
  memoryEnabled: boolean;
  memoryInjectionEnabled: boolean;
  memoryIncognito: boolean;
  /**
   * Team memory (EPIC-07 section 5, Max only). Default false. Enabling is
   * server-enforced: a Pro seat gets 403 MAX_TIER_REQUIRED from
   * PUT /api/memory/settings. Recall widens to team facts only while this is
   * true on the server.
   */
  memoryMultiplayerEnabled: boolean;
  memoryPlatforms: string[];
  memoryRetentionDays: number;
  /** Range 150-1200, default 800 (contract section 5). */
  memoryPackTokenBudget: number;
  // ---- v2 keys (contract section 5). Optional on read: an older API omits them.
  memoryProfileEnabled?: boolean;
  memoryProfileTokenBudget?: number;
  memoryNotesTokenBudget?: number;
  memoryProviderPolicy?: Record<string, MemorySensitivity>;
  /** Notes in these domains are recalled only into conversations on the same topic. */
  memorySensitiveDomains?: MemoryDomain[];
  memoryConsolidateOnACOnly?: boolean;
  memoryImportEnabled?: boolean;
}

export interface MemoryStats {
  facts: Record<string, number>;
  factsByCategory: Record<string, number>;
  episodes: Record<string, number>;
  identity: { total: number; hasKey: boolean; byType: { entity_type: string; count: number }[] };
  settings: MemorySettings;
}

export interface MemoryAuditEntry {
  id: string;
  action: string;
  detail: Record<string, unknown>;
  timestamp: number;
}

/** One line of the recall "why" list (contract section 1, notes.explain). */
export interface MemoryRecallExplain {
  factId: string;
  channels: string[];
  score: number;
}

export interface MemoryProfileBlock {
  label: MemoryProfileLabel;
  description?: string;
  value: string;
  charLimit: number;
  version: number;
  confidence: number;
  locked: boolean;
  updatedAt: number | null;
  updatedBy: 'reflection' | 'user' | 'migration' | null;
  sourceFactIds?: string[];
}

export interface MemoryEvidence {
  quote: string;
  platform: string | null;
  observedAt: number | null;
}

export interface MemoryEntityCard {
  twinHash: string;
  /** Reverted (real) value when the vault could resolve it, else the twin. */
  displayValue: string;
  entityType: string | null;
  factCount: number;
  lastMentionedAt: number | null;
  mentionCount: number;
  summary: string | null;
  aliases: string[];
}

function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function asStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  if (typeof v === 'string') {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }
  return [];
}

function emptyBlock(label: MemoryProfileLabel): MemoryProfileBlock {
  return {
    label, value: '', charLimit: MEMORY_PROFILE_CHAR_LIMITS[label], version: 0, confidence: 0,
    locked: false, updatedAt: null, updatedBy: null, sourceFactIds: [],
  };
}

function normalizeBlock(raw: Record<string, unknown>, fallbackLabel?: MemoryProfileLabel): MemoryProfileBlock | null {
  const label = (typeof raw.label === 'string' ? raw.label : fallbackLabel) as MemoryProfileLabel | undefined;
  if (!label || !(MEMORY_PROFILE_LABELS as readonly string[]).includes(label)) return null;
  return {
    label,
    description: typeof raw.description === 'string' ? raw.description : undefined,
    value: typeof raw.value === 'string' ? raw.value : '',
    charLimit: asNumber(raw.charLimit) ?? MEMORY_PROFILE_CHAR_LIMITS[label],
    version: asNumber(raw.version) ?? 0,
    confidence: asNumber(raw.confidence) ?? 0,
    locked: raw.locked === true,
    updatedAt: asNumber(raw.updatedAt),
    updatedBy: (raw.updatedBy as MemoryProfileBlock['updatedBy']) ?? null,
    sourceFactIds: asStringArray(raw.sourceFactIds),
  };
}

/**
 * GET /api/memory/profile answers "4 blocks (reverted for display)". Accept
 * either an array of blocks or an object keyed by label, and always hand back
 * all four labels so the editor can render an empty card for a missing one.
 */
export function normalizeProfile(payload: unknown): MemoryProfileBlock[] {
  const byLabel = new Map<MemoryProfileLabel, MemoryProfileBlock>();
  const body = (payload && typeof payload === 'object' && 'blocks' in (payload as Record<string, unknown>))
    ? (payload as Record<string, unknown>).blocks
    : payload;
  if (Array.isArray(body)) {
    for (const raw of body) {
      if (raw && typeof raw === 'object') {
        const block = normalizeBlock(raw as Record<string, unknown>);
        if (block) byLabel.set(block.label, block);
      }
    }
  } else if (body && typeof body === 'object') {
    for (const label of MEMORY_PROFILE_LABELS) {
      const raw = (body as Record<string, unknown>)[label];
      if (raw && typeof raw === 'object') {
        const block = normalizeBlock(raw as Record<string, unknown>, label);
        if (block) byLabel.set(label, block);
      } else if (typeof raw === 'string') {
        byLabel.set(label, { ...emptyBlock(label), value: raw });
      }
    }
  }
  return MEMORY_PROFILE_LABELS.map((label) => byLabel.get(label) ?? emptyBlock(label));
}

/** GET /api/memory/entities answers "entity cards (reverted)"; be lenient about field names. */
export function normalizeEntityCard(raw: Record<string, unknown>): MemoryEntityCard | null {
  const twinHash = [raw.twinHash, raw.twin_hash, raw.id].find((v): v is string => typeof v === 'string' && v.length > 0);
  if (!twinHash) return null;
  const displayValue = [raw.displayValue, raw.value, raw.original, raw.name, raw.twin]
    .find((v): v is string => typeof v === 'string' && v.length > 0) ?? twinHash;
  return {
    twinHash,
    displayValue,
    entityType: typeof raw.entityType === 'string' ? raw.entityType : null,
    factCount: asNumber(raw.factCount) ?? asNumber(raw.summaryFactCount) ?? 0,
    lastMentionedAt: asNumber(raw.lastMentionedAt) ?? asNumber(raw.lastSeenAt),
    mentionCount: asNumber(raw.mentionCount) ?? 0,
    summary: typeof raw.summary === 'string' && raw.summary ? raw.summary : null,
    aliases: asStringArray(raw.aliases),
  };
}

class MemoryService {
  async getFacts(params: { category?: string; all?: boolean; limit?: number; status?: MemoryStatus } = {}): Promise<MemoryFact[]> {
    const res = await axios.get(`${API_BASE_URL}/api/memory`, {
      headers: authHeaders(),
      params: {
        ...(params.category ? { category: params.category } : {}),
        ...(params.all ? { all: 'true' } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.status ? { status: params.status } : {}),
      },
    });
    return res.data?.data || [];
  }

  async getStats(): Promise<MemoryStats> {
    const res = await axios.get(`${API_BASE_URL}/api/memory/stats`, { headers: authHeaders() });
    return res.data?.data;
  }

  async getSettings(): Promise<MemorySettings> {
    const res = await axios.get(`${API_BASE_URL}/api/memory/settings`, { headers: authHeaders() });
    return res.data?.data;
  }

  async updateSettings(patch: Partial<MemorySettings>): Promise<MemorySettings> {
    const res = await axios.put(`${API_BASE_URL}/api/memory/settings`, patch, { headers: authHeaders() });
    return res.data?.data;
  }

  async deleteFact(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/memory/facts/${encodeURIComponent(id)}`, { headers: authHeaders() });
  }

  async correctFact(id: string, text: string): Promise<string> {
    const res = await axios.post(
      `${API_BASE_URL}/api/memory/facts/${encodeURIComponent(id)}/correct`,
      { text },
      { headers: authHeaders() },
    );
    return res.data?.id;
  }

  /** POST /api/memory/facts/:id/pin — the body carries the target state. */
  async pinFact(id: string, pinned: boolean): Promise<void> {
    await axios.post(
      `${API_BASE_URL}/api/memory/facts/${encodeURIComponent(id)}/pin`,
      { pinned },
      { headers: authHeaders() },
    );
  }

  async archiveFact(id: string): Promise<void> {
    await axios.post(`${API_BASE_URL}/api/memory/facts/${encodeURIComponent(id)}/archive`, {}, { headers: authHeaders() });
  }

  async restoreFact(id: string): Promise<void> {
    await axios.post(`${API_BASE_URL}/api/memory/facts/${encodeURIComponent(id)}/restore`, {}, { headers: authHeaders() });
  }

  /** GET /api/memory/facts/:id/evidence → the reverted quote the fact was distilled from. */
  async getEvidence(id: string): Promise<MemoryEvidence> {
    const res = await axios.get(`${API_BASE_URL}/api/memory/facts/${encodeURIComponent(id)}/evidence`, { headers: authHeaders() });
    const d = res.data?.data ?? res.data ?? {};
    return {
      quote: typeof d.quote === 'string' ? d.quote : '',
      platform: typeof d.platform === 'string' ? d.platform : null,
      observedAt: asNumber(d.observedAt),
    };
  }

  /** POST /api/memory/remember — a memory the user typed in themselves (sourceRole user-manual). */
  async remember(text: string, category?: MemoryCategoryV2): Promise<{ factId: string; status: MemoryStatus }> {
    const res = await axios.post(
      `${API_BASE_URL}/api/memory/remember`,
      { text, ...(category ? { category } : {}) },
      { headers: authHeaders() },
    );
    return res.data?.data ?? res.data;
  }

  // ---- Profile (the four compiled blocks)
  async getProfile(): Promise<MemoryProfileBlock[]> {
    const res = await axios.get(`${API_BASE_URL}/api/memory/profile`, { headers: authHeaders() });
    return normalizeProfile(res.data?.data ?? res.data);
  }

  async updateProfileBlock(label: MemoryProfileLabel, patch: { value: string; locked?: boolean }): Promise<void> {
    await axios.put(`${API_BASE_URL}/api/memory/profile/${encodeURIComponent(label)}`, patch, { headers: authHeaders() });
  }

  /** Enqueue a reflection run now instead of waiting for the next scheduled one. */
  async refreshProfile(): Promise<void> {
    await axios.post(`${API_BASE_URL}/api/memory/profile/refresh`, {}, { headers: authHeaders() });
  }

  // ---- People & projects
  async getEntities(): Promise<MemoryEntityCard[]> {
    const res = await axios.get(`${API_BASE_URL}/api/memory/entities`, { headers: authHeaders() });
    const rows: unknown = res.data?.data ?? res.data;
    if (!Array.isArray(rows)) return [];
    return rows
      .map((r) => (r && typeof r === 'object' ? normalizeEntityCard(r as Record<string, unknown>) : null))
      .filter((c): c is MemoryEntityCard => c !== null);
  }

  /** Forget everything about one entity: its facts and its card. */
  async forgetEntity(twinHash: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/memory/entities/${encodeURIComponent(twinHash)}`, { headers: authHeaders() });
  }

  /** Forget every fact and episode in one domain (e.g. health). Recompiles the profile server-side. */
  async forgetDomain(domain: MemoryDomain): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/memory/domains/${encodeURIComponent(domain)}`, { headers: authHeaders() });
  }

  // ---- Timeline (event facts)
  async getTimeline(range: { from?: number; to?: number } = {}): Promise<MemoryFact[]> {
    const res = await axios.get(`${API_BASE_URL}/api/memory/timeline`, {
      headers: authHeaders(),
      params: {
        ...(range.from ? { from: range.from } : {}),
        ...(range.to ? { to: range.to } : {}),
      },
    });
    return res.data?.data || [];
  }

  // ---- Import ("memory that follows you")
  /**
   * The portal never sends real text to the memory plane: the pasted export is
   * encoded first through the same scanner endpoint every other client uses,
   * and only the twin-space result reaches POST /api/memory/import.
   */
  async encodeForImport(text: string, platform: MemoryImportPlatform): Promise<string> {
    const res = await axios.post(
      `${API_BASE_URL}/api/scanner/encode`,
      { data: text, sourceContainer: 'memory-import', sourceElement: `import:${platform}` },
      { headers: authHeaders() },
    );
    const encoded = res.data?.encodedData;
    if (typeof encoded !== 'string' || !encoded) {
      throw new Error('encode-unavailable');
    }
    return encoded;
  }

  async importMemory(platform: MemoryImportPlatform, promptTwin: string): Promise<void> {
    await axios.post(`${API_BASE_URL}/api/memory/import`, { platform, promptTwin }, { headers: authHeaders() });
  }

  /** Team memory (EPIC-07, Max only): the company's shared domain-scope facts. */
  async getTeamFacts(limit = 200): Promise<MemoryFact[]> {
    const res = await axios.get(`${API_BASE_URL}/api/memory/team`, {
      headers: authHeaders(),
      params: { limit },
    });
    return res.data?.data || [];
  }

  /** Promote one of the caller's own facts to team (domain) scope. Max only. */
  async shareFact(id: string): Promise<string> {
    const res = await axios.post(
      `${API_BASE_URL}/api/memory/facts/${encodeURIComponent(id)}/share`,
      {},
      { headers: authHeaders() },
    );
    return res.data?.id;
  }

  /** Delete a shared team fact (promoter or company admin). Max only. */
  async deleteTeamFact(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/api/memory/team/${encodeURIComponent(id)}`, { headers: authHeaders() });
  }

  async getAudit(limit = 100): Promise<MemoryAuditEntry[]> {
    const res = await axios.get(`${API_BASE_URL}/api/memory/audit`, {
      headers: authHeaders(),
      params: { limit },
    });
    return res.data?.data || [];
  }

  async exportMemory(): Promise<Blob> {
    const res = await axios.get(`${API_BASE_URL}/api/memory/export`, {
      headers: authHeaders(),
      responseType: 'blob',
    });
    return res.data;
  }

  /** Irreversible: crypto-shreds the vault key and deletes the memory graph. */
  async eraseMemory(): Promise<void> {
    await axios.post(`${API_BASE_URL}/api/memory/erase`, { confirm: 'ERASE' }, { headers: authHeaders() });
  }
}

export const memoryService = new MemoryService();
export default memoryService;
